import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { resolveImage } from "../lib/data";

/**
 * HeroVideoBackground — a resilient cinematic background layer.
 *
 * Design goals (in order of priority):
 *   1. Never break the hero. If anything fails, the poster stays.
 *   2. Never waste user data. Respect Save-Data, slow connections,
 *      reduced-motion preference, and hidden tab (battery).
 *   3. Never flicker. Fade the poster → video only after the first
 *      frames are actually decoded and ready to play.
 *
 * Buffering strategy (in order):
 *   • Instant paint: <img> poster shown immediately (no waiting).
 *   • Progressive: <video preload="metadata"> — we let the browser
 *     pull just enough to know dimensions and duration.
 *   • Ready gate: we listen for `canplaythrough`. Only when the
 *     browser signals it can play through WITHOUT stalling do we
 *     start playback and fade the video over the poster.
 *   • Safety net: 8s watchdog. If canplaythrough never fires,
 *     we start playback anyway (better than a black hero) but
 *     only after `canplay`. If canplay also fails, we stay on
 *     poster forever — no error, no console noise.
 *   • Loop cleanup: on `ended` we set currentTime=0 and play again
 *     (belt + suspenders alongside the `loop` attribute; fixes
 *     Safari's occasional loop hiccup).
 *   • Visibility pause: when the tab is hidden we pause; when it
 *     comes back and the video is ready, we resume.
 *
 * Data-frugality gates (any → skip video, keep poster):
 *   • Save-Data: navigator.connection?.saveData === true
 *   • Slow network: effectiveType in {'2g','slow-2g','3g'}
 *   • Reduced motion: matchMedia('(prefers-reduced-motion: reduce)')
 *   • Mobile: if playOnMobile is false (default) and pointer:coarse
 *
 * Props:
 *   • src        (mp4)          – primary source, required
 *   • srcWebm    (webm, opt)    – smaller/newer codec first in <source>
 *   • poster     (image url)    – required as fallback
 *   • objectFit  'cover'|'contain'
 *   • playOnMobile bool         – opt-in for mobile playback
 *   • overlayClassName          – Tailwind classes for dark wash
 */
export default function HeroVideoBackground({
  src,
  srcWebm = "",
  poster,
  objectFit = "cover",
  playOnMobile = false,
  overlayClassName = "",
  "data-testid": testId = "hero-video-background",
}) {
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false); // decoded first frames, safe to show
  const [enabled, setEnabled] = useState(false); // passed the data-frugality gates

  const resolvedPoster = resolveImage(poster) || "";
  const resolvedMp4 = resolveImage(src) || "";
  const resolvedWebm = resolveImage(srcWebm) || "";

  /* ---------- gates ---------- */
  useEffect(() => {
    if (!resolvedMp4 && !resolvedWebm) {
      setEnabled(false);
      return;
    }
    if (typeof window === "undefined") return;

    // 1. Reduced motion
    const reduce = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    )?.matches;
    if (reduce) return setEnabled(false);

    // 2. Save-Data / slow connection
    const conn =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;
    if (conn?.saveData) return setEnabled(false);
    const slow =
      conn?.effectiveType &&
      ["slow-2g", "2g", "3g"].includes(conn.effectiveType);
    if (slow) return setEnabled(false);

    // 3. Mobile opt-out
    const coarse = window.matchMedia?.("(pointer: coarse)")?.matches;
    if (coarse && !playOnMobile) return setEnabled(false);

    setEnabled(true);
  }, [resolvedMp4, resolvedWebm, playOnMobile]);

  /* ---------- readiness ---------- */
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !enabled) return;

    let watchdog;
    let readyFired = false;

    const markReady = () => {
      if (readyFired) return;
      readyFired = true;
      setReady(true);
      // Kick off playback (autoplay + muted + playsinline required for
      // iOS Safari to allow it without user gesture).
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };

    const onCanPlayThrough = () => markReady();
    const onCanPlay = () => {
      // Fallback: if canplaythrough never fires within 8s, we go on canplay.
      if (!readyFired && v.readyState >= 3) {
        // small delay so the first frame is definitely decoded
        setTimeout(markReady, 120);
      }
    };
    const onEnded = () => {
      // Safari loop hiccup safety net.
      try {
        v.currentTime = 0;
        v.play().catch(() => {});
      } catch (_) {}
    };
    const onError = () => {
      // Silently keep the poster.
      setReady(false);
    };

    v.addEventListener("canplaythrough", onCanPlayThrough);
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("ended", onEnded);
    v.addEventListener("error", onError);

    // Watchdog — if the browser is slow to buffer, at 8s we accept
    // whatever is there rather than waiting forever.
    watchdog = window.setTimeout(() => {
      if (!readyFired && v.readyState >= 2) markReady();
    }, 8000);

    // Bandwidth strategy: we KEEP preload="metadata" until the browser
    // has signalled it has some usable data (readyState >= HAVE_CURRENT_DATA).
    // Only then do we upgrade to "auto" so it fills the buffer aggressively
    // for smooth looping. This preserves the intent of "metadata first,
    // upgrade later" and gives borderline connections a chance to bail
    // (via the reduced-motion / save-data / slow-network gates) before we
    // pull the full asset.
    const upgradePreloadWhenSafe = () => {
      if (v.readyState >= 2) {
        try {
          v.preload = "auto";
        } catch (_) {}
      }
    };
    v.addEventListener("loadeddata", upgradePreloadWhenSafe, { once: true });

    return () => {
      v.removeEventListener("canplaythrough", onCanPlayThrough);
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("ended", onEnded);
      v.removeEventListener("error", onError);
      v.removeEventListener("loadeddata", upgradePreloadWhenSafe);
      window.clearTimeout(watchdog);
    };
  }, [enabled, resolvedMp4, resolvedWebm]);

  /* ---------- battery: pause when tab hidden ---------- */
  useEffect(() => {
    if (!enabled) return;
    const onVis = () => {
      const v = videoRef.current;
      if (!v) return;
      if (document.hidden) {
        v.pause();
      } else if (ready) {
        v.play().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [enabled, ready]);

  /* ---------- render ---------- */
  const objectFitClass = objectFit === "contain" ? "object-contain" : "object-cover";

  return (
    <div
      data-testid={testId}
      className="absolute inset-0 overflow-hidden bg-[#0A0908]"
    >
      {/* Poster — always rendered, always instant */}
      {resolvedPoster && (
        <img
          src={resolvedPoster}
          alt=""
          aria-hidden="true"
          className={`absolute inset-0 w-full h-full ${objectFitClass}`}
          loading="eager"
          decoding="async"
          data-testid={`${testId}-poster`}
        />
      )}

      {/* Video — mounted only if gates pass */}
      {enabled && (resolvedMp4 || resolvedWebm) && (
        <AnimatePresence>
          <motion.video
            key="hero-bg-video"
            ref={videoRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: ready ? 1 : 0 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className={`absolute inset-0 w-full h-full ${objectFitClass} will-change-[opacity]`}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            // NOTE: no `controls`, no `download`; we don't want the
            // native UI in a background element.
            poster={resolvedPoster || undefined}
            data-testid={`${testId}-video`}
          >
            {resolvedWebm && <source src={resolvedWebm} type="video/webm" />}
            {resolvedMp4 && <source src={resolvedMp4} type="video/mp4" />}
          </motion.video>
        </AnimatePresence>
      )}

      {/* Optional wash overlay for text legibility */}
      {overlayClassName && (
        <div
          aria-hidden="true"
          className={`absolute inset-0 pointer-events-none ${overlayClassName}`}
        />
      )}
    </div>
  );
}
